import React from "react";

interface FieldConfig<T> {
  key: keyof T;
  label: string;
  multiline?: boolean;
}

// Generic add/remove/reorder editor for an array of small flat objects —
// covers trustStats ({value,label}), overviewStats ({label,value}),
// advantages/processSteps ({title,description}), investmentTiers
// ({label,amount}) with one component instead of four near-identical ones.
export default function ObjectListEditor<T extends Record<string, string>>(props: {
  label: string;
  values: T[];
  fields: FieldConfig<T>[];
  emptyItem: T;
  onChange: (values: T[]) => void;
}) {
  const { values, fields, emptyItem, onChange } = props;

  function updateField(index: number, key: keyof T, value: string) {
    const next = [...values];
    next[index] = { ...next[index], [key]: value };
    onChange(next);
  }

  function remove(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= values.length) return;
    const next = [...values];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  return (
    <div className="mb-4">
      <span className="block text-sm text-secondary-dark mb-1.5">{props.label}</span>
      <div className="flex flex-col gap-3">
        {values.map((item, index) => (
          <div key={index} className="border border-fourth-light rounded-lg p-3 bg-c-gray/40">
            <div className="flex items-start gap-2">
              <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, 1fr)` }}>
                {fields.map((field) =>
                  field.multiline ? (
                    <textarea
                      key={String(field.key)}
                      value={item[field.key] ?? ""}
                      placeholder={field.label}
                      rows={2}
                      onChange={(e) => updateField(index, field.key, e.target.value)}
                      className="border border-fourth-light rounded-md px-2 py-1.5 text-sm outline-none focus:border-gold transition-colors resize-y bg-white"
                    />
                  ) : (
                    <input
                      key={String(field.key)}
                      type="text"
                      value={item[field.key] ?? ""}
                      placeholder={field.label}
                      onChange={(e) => updateField(index, field.key, e.target.value)}
                      className="border border-fourth-light rounded-md px-2 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
                    />
                  ),
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" onClick={() => move(index, -1)} className="text-secondary-light hover:text-gold text-xs" title="上移">↑</button>
                <button type="button" onClick={() => move(index, 1)} className="text-secondary-light hover:text-gold text-xs" title="下移">↓</button>
                <button type="button" onClick={() => remove(index)} className="text-red-400 hover:text-red-600 text-xs" title="删除">✕</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...values, emptyItem])}
        className="mt-2 text-xs text-gold hover:text-secondary-dark transition-colors"
      >
        + 添加一项
      </button>
    </div>
  );
}
